import NiceModal from '@ebay/nice-modal-react'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined'
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined'
import {
  Avatar,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  SortDirection,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { CompanyUsersStatus } from '@shared/types/companies.types'
import { Permission, Role } from '@shared/types/roles.types'
import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import EmptySearchResults from '../../components/EmptySearchResults'
import SearchInput from '../../components/forms/SearchInput'
import TableLoader from '../../components/loaders/TableLoader'
import { AssignRoleModal } from '../../components/modals/AssignRoleModal'
import { DeleteRoleModal } from '../../components/modals/DeleteRoleModal'
import { InvalidDeleteRoleModal } from '../../components/modals/InvalidDeleteRoleModal'
import { useAuthStore } from '../../hooks/useAuthStore'
import {
  useCompanyRoles,
  useCompanyUser,
  useCompanyUsers
} from '../../hooks/useQueries/useCompanies'
import { getPaletteColor } from '../../utils/theme.utils'
import { isDefaultRole } from '../../utils/user.utils'

type RolesSortColumn = 'users' | 'roles' | ''

export default function CompaniesRoles() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const companyId = params.companyId!
  const [searchText, setSearchText] = useState('')
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([])
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [lastAppliedFilters, setLastAppliedFilters] = useState({
    searchText: ''
  })
  const [orderBy, setOrderBy] = useState<RolesSortColumn>('')
  const [orderDirection, setOrderDirection] = useState<SortDirection>('asc')
  const { userId } = useAuthStore()
  const { data: user, isLoading: isCompanyUserLoading } = useCompanyUser(
    companyId,
    userId!
  )
  const { data: roles, isLoading: isRolesLoading } = useCompanyRoles(companyId)
  const { data: members, isLoading: isMembersLoading } = useCompanyUsers(
    companyId,
    {
      status: CompanyUsersStatus.ACTIVE
    }
  )
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)

  const roleMemberCounts = useMemo(() => {
    if (!members || !roles) return new Map()

    const counts = new Map<string, number>()
    roles.forEach(role => {
      const count = members.filter(member => member.roleId === role.id).length
      counts.set(role.id, count)
    })
    return counts
  }, [members, roles])

  const { canManageRoles, canUpdateUsers } = useMemo(() => {
    return {
      canManageRoles:
        user?.permissions?.includes(Permission.ROLES) || user?.role?.fullAccess,
      canUpdateUsers:
        user?.permissions?.includes(Permission.USERS_UPDATE) ||
        user?.role?.fullAccess
    }
  }, [user])

  useEffect(() => {
    if (user && !canManageRoles) {
      navigate('/access-denied')
    }
  }, [canManageRoles, navigate, user])

  useEffect(() => {
    setFilteredRoles(roles || [])
  }, [roles])

  const handleFilter = (searchValue: string) => {
    const filtered = roles?.filter(role =>
      role.name.toLowerCase().includes(searchValue.toLowerCase())
    )
    setFilteredRoles(filtered || [])
    setLastAppliedFilters({ searchText: searchValue })
  }

  const areFiltersEqual = (
    current: typeof lastAppliedFilters,
    last: typeof lastAppliedFilters
  ) => {
    return current.searchText === last.searchText
  }

  const handleMenuClick = (event: MouseEvent<HTMLElement>, role: Role) => {
    setAnchorEl(event.currentTarget)
    setSelectedRole(role)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setSelectedRole(null)
  }

  const handleSort = (column: RolesSortColumn) => {
    let newOrderDirection: SortDirection = 'asc'

    if (orderBy === column) {
      newOrderDirection = orderDirection === 'asc' ? 'desc' : 'asc'
    }

    setOrderBy(column)
    setOrderDirection(newOrderDirection)

    const sorted = [...filteredRoles].sort((a, b) => {
      const getValue = (role: Role) => {
        switch (column) {
          case 'roles':
            return role.name.toLowerCase()
          case 'users':
            return roleMemberCounts.get(role.id) ?? 0
          default:
            return ''
        }
      }

      const valA = getValue(a)
      const valB = getValue(b)

      if (typeof valA === 'string' && typeof valB === 'string') {
        return newOrderDirection === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      } else {
        return newOrderDirection === 'asc'
          ? (valA as number) - (valB as number)
          : (valB as number) - (valA as number)
      }
    })

    setFilteredRoles(sorted)
  }

  if (isRolesLoading || isMembersLoading || isCompanyUserLoading) {
    return <TableLoader totalRows={3} totalColumns={2} />
  }

  return (
    <TableContainer>
      <Stack direction='row' justifyContent='space-between' paddingY='1rem'>
        <Stack
          direction='row'
          spacing='1rem'
          alignItems='center'
          paddingBottom='1rem'
        >
          <SearchInput
            searchText={searchText}
            setSearchText={setSearchText}
            handleFilter={handleFilter}
          />
          <Button
            variant='text'
            color='primary'
            onClick={() => handleFilter(searchText)}
            disabled={
              !searchText || areFiltersEqual({ searchText }, lastAppliedFilters)
            }
            sx={{ textTransform: 'none' }}
          >
            {t('common.search')}
          </Button>
          {searchText && (
            <Button
              startIcon={<CloseIcon />}
              onClick={() => {
                setSearchText('')
                setFilteredRoles(roles || [])
                setLastAppliedFilters({
                  searchText: ''
                })
              }}
              sx={{
                textTransform: 'none',
                color: 'secondary.main'
              }}
            >
              {t('common.delete_filters')}
            </Button>
          )}
        </Stack>
        {canManageRoles && (
          <Button
            variant='text'
            color='primary'
            startIcon={<PersonPinOutlinedIcon />}
            component={Link}
            to={`/companies/${companyId}/roles/create`}
            sx={{ textTransform: 'none', fontSize: '1rem' }}
          >
            {t('roles_view.create_role')}
          </Button>
        )}
      </Stack>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sortDirection={orderBy === 'roles' ? orderDirection : false}
            >
              <TableSortLabel
                active={orderBy === 'roles'}
                direction={
                  orderBy === 'roles' && orderDirection !== false
                    ? (orderDirection as 'asc' | 'desc')
                    : undefined
                }
                hideSortIcon={false}
                onClick={() => handleSort('roles')}
              >
                {t('roles_view.table_headers.roles')}
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={orderBy === 'users' ? orderDirection : false}
            >
              <TableSortLabel
                active={orderBy === 'users'}
                direction={
                  orderBy === 'users' && orderDirection !== false
                    ? (orderDirection as 'asc' | 'desc')
                    : undefined
                }
                hideSortIcon={false}
                onClick={() => handleSort('users')}
              >
                {t('roles_view.table_headers.users')}
              </TableSortLabel>
            </TableCell>
            <TableCell align='right'>
              {t('roles_view.table_headers.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRoles.length ? (
            filteredRoles.map(role => {
              const color = getPaletteColor(role.color)

              return (
                <TableRow key={role.id}>
                  <TableCell>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <Avatar sx={{ bgcolor: color.hex }}>
                        <PersonPinOutlinedIcon sx={{ color: color.contrast }} />
                      </Avatar>
                      <Typography
                        variant='body2'
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {isDefaultRole(role.name)
                          ? t(`role.${role.name}`)
                          : role.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      <Typography variant='body2'>
                        {roleMemberCounts.get(role.id) ?? 0}
                      </Typography>
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton
                      onClick={e => handleMenuClick(e, role)}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.12)'
                      }}
                    >
                      <MoreHorizIcon color='action' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })
          ) : (
            <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>
              <TableCell colSpan={3}>
                <EmptySearchResults />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleCloseMenu}>
          <MenuList>
            <MenuItem
              component={Link}
              to={`/companies/${companyId}/roles/${selectedRole?.id}`}
              onClick={handleCloseMenu}
            >
              <ListItemIcon>
                <RemoveRedEyeOutlinedIcon fontSize='small' />
              </ListItemIcon>
              <ListItemText>{t('roles_view.menu.view')}</ListItemText>
            </MenuItem>
            {canManageRoles && (
              <MenuItem
                component={Link}
                to={`/companies/${companyId}/roles/${selectedRole?.id}/edit`}
                onClick={handleCloseMenu}
              >
                <ListItemIcon>
                  <DriveFileRenameOutlineOutlinedIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>{t('roles_view.menu.edit')}</ListItemText>
              </MenuItem>
            )}
            {canUpdateUsers && (
              <MenuItem
                onClick={() => {
                  if (selectedRole) {
                    NiceModal.show(AssignRoleModal, {
                      users: members || [],
                      role: selectedRole,
                      companyId
                    })
                  }
                  handleCloseMenu()
                }}
              >
                <ListItemIcon>
                  <PersonAddAltIcon fontSize='small' />
                </ListItemIcon>
                <ListItemText>{t('roles_view.menu.add_user')}</ListItemText>
              </MenuItem>
            )}
            {canManageRoles && (
              <MenuItem
                onClick={() => {
                  if (selectedRole) {
                    const assignedUsers =
                      roleMemberCounts.get(selectedRole.id) ?? 0

                    if (assignedUsers > 0) {
                      NiceModal.show(InvalidDeleteRoleModal)
                    } else {
                      NiceModal.show(DeleteRoleModal, {
                        companyId,
                        roleId: selectedRole.id,
                        roleName: selectedRole.name
                      })
                    }
                  }
                  handleCloseMenu()
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineOutlinedIcon fontSize='small' color='error' />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>
                  {t('common.delete')}
                </ListItemText>
              </MenuItem>
            )}
          </MenuList>
        </Menu>
      </Table>
    </TableContainer>
  )
}
